/**
 * @file useScanPace.ts
 * @description Определение источника ввода поля склада по темпу набора
 * (phase19.23).
 *
 * В мобильном приложении источник известен явно (камера или клавиатура), в
 * вебе USB-сканер неотличим от клавиатуры — он и есть клавиатура. Поэтому
 * смотрим на скорость: сканер выдаёт «пачку» символов за миллисекунды.
 *
 * Хук считает только последнюю пачку: если кладовщик оставил в поле текст
 * фильтра и поверх него сканировал, на сервер уйдёт именно отсканированный
 * хвост, а не смесь «фильтр + код».
 */

import { useCallback, useRef } from "react";

import { isScannerPace } from "@/lib/picking-search";

/** Пауза между символами, после которой начинается новая пачка ввода, мс. */
const BURST_GAP_MS = 120;

/** Результат разбора отправляемого значения. */
export interface ScanPaceRead {
  /** Символы последней пачки — то, что реально «напечатал» сканер. */
  burst: string;
  /** true — пачка набрана со скоростью сканера. */
  fromScanner: boolean;
}

export interface ScanPace {
  /** Зафиксировать изменение значения поля (до/после). */
  track: (next: string, prev: string) => void;
  /** Разобрать значение на отправке: что за код и откуда пришёл. */
  read: (value: string) => ScanPaceRead;
  /** Сбросить накопленное (очистка поля, отправка). */
  reset: () => void;
}

export function useScanPace(): ScanPace {
  // Начало текущей пачки: время и позиция в строке, с которой она началась.
  const burst = useRef({ startedAt: 0, startIndex: 0, lastAt: 0 });

  const track = useCallback((next: string, prev: string) => {
    const now = Date.now();
    const state = burst.current;
    // Новая пачка: после паузы либо после удаления символов (правка руками).
    if (now - state.lastAt > BURST_GAP_MS || next.length <= prev.length) {
      state.startedAt = now;
      state.startIndex = Math.min(prev.length, next.length);
    }
    state.lastAt = now;
  }, []);

  const read = useCallback((value: string): ScanPaceRead => {
    const state = burst.current;
    const start = Math.min(state.startIndex, value.length);
    const tail = value.slice(start);
    return {
      burst: tail,
      fromScanner: isScannerPace(tail.length, Date.now() - state.startedAt),
    };
  }, []);

  const reset = useCallback(() => {
    burst.current = { startedAt: 0, startIndex: 0, lastAt: 0 };
  }, []);

  return { track, read, reset };
}
